import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { selfApi } from '../../services/selfApi.js';
import { BalanceTiles, WeekGrid } from '../hr/WeekGrid.jsx';
import { MonthBar } from './MonthBar.jsx';
import { Stempeluhr } from './Stempeluhr.jsx';
import { TimeAccountCard } from './TimeAccountCard.jsx';

/**
 * Meine Zeit.
 *
 * Zwei Wege, absichtlich beide: Kommen und Gehen für die belegbare
 * Erfassung, eine getippte Zahl für Nachträge. Wer einen vergessenen
 * Dienstag nachträgt, soll keine Uhrzeiten erfinden müssen.
 */

/** Montag der Woche, in der dieses Datum liegt - in UTC gerechnet. */
function mondayOf(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  const iso = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  return new Date(d.getTime() - (iso - 1) * 86_400_000).toISOString().slice(0, 10);
}

const shift = (isoDate, days) =>
  new Date(new Date(`${isoDate}T00:00:00Z`).getTime() + days * 86_400_000).toISOString().slice(0, 10);

const heute = () => new Date().toISOString().slice(0, 10);

const LEER = { workDate: '', startTime: '08:00', endTime: '17:00', breakMinutes: 30, hours: '', crossesMidnight: false };

export function MyTimePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [from, setFrom] = useState(() => mondayOf(heute()));
  const [modus, setModus] = useState('clock'); // clock | hours
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(LEER);
  const [error, setError] = useState(null);

  const to = shift(from, 6);
  /* Der Monat der angezeigten Woche. Blättert man die Woche, folgt der Monat -
     das ist vorhersehbar, und eine zweite Navigation daneben wäre eine
     Bedienung zu viel für eine Frage, die man einmal im Monat stellt. */
  const jahr = Number(from.slice(0, 4));
  const monat = Number(from.slice(5, 7));

  const profile = useQuery({ queryKey: ['self', 'profile'], queryFn: () => selfApi.profile() });
  const week = useQuery({
    queryKey: ['self', 'week', from],
    queryFn: () => selfApi.week({ from, to }),
    enabled: Boolean(profile.data),
  });
  const month = useQuery({
    queryKey: ['self', 'month', jahr, monat],
    queryFn: () => selfApi.month({ year: jahr, month: monat }),
    enabled: Boolean(profile.data),
  });
  /* Das Stundenkonto hängt NICHT an der angezeigten Woche. Es ist der
     Gesamtstand bis heute; ihn beim Blättern neu zu laden würde ihn
     aussehen lassen, als änderte er sich mit der Ansicht. */
  const account = useQuery({
    queryKey: ['self', 'timeAccount'],
    queryFn: () => selfApi.timeAccount(),
    enabled: Boolean(profile.data),
  });
  /* Was die Firma erlaubt. Dieselbe Abfrage wie in der Stempeluhr - React
     Query gibt beiden denselben Eintrag, es geht also kein zweiter Aufruf
     hinaus. Die Antwort entscheidet nur, welche Knoepfe hier stehen; DASS
     die Regel gilt, setzt der Dienst durch. */
  const stempel = useQuery({
    queryKey: ['self', 'stempel'],
    queryFn: () => selfApi.stempelStand(),
    enabled: Boolean(profile.data),
  });
  const nurStempel = stempel.data?.modus === 'NUR_STEMPEL';

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['self', 'week'] });
    // Ohne diese Zeile zeigte die Leiste nach jeder Erfassung weiter die
    // alten Monatszahlen - der Saldo stimmte, die Leiste darüber nicht.
    queryClient.invalidateQueries({ queryKey: ['self', 'month'] });
    /* Und das Konto: jede erfasste Stunde ändert es. Ein Kontostand, der
       erst nach einem Neuladen der Seite stimmt, ist schlimmer als keiner -
       man glaubt ihm, und er ist falsch. */
    queryClient.invalidateQueries({ queryKey: ['self', 'timeAccount'] });
  };

  const add = useMutation({
    mutationFn: () =>
      selfApi.logTime(
        modus === 'clock'
          ? {
              workDate: form.workDate,
              startTime: form.startTime,
              endTime: form.endTime,
              breakMinutes: Number(form.breakMinutes) || 0,
              crossesMidnight: form.crossesMidnight,
            }
          : { workDate: form.workDate, hours: Number(form.hours) },
      ),
    onSuccess: () => {
      refresh();
      setOpen(false);
      setError(null);
    },
    onError: (err) => setError(err?.message || t('time.add.failed')),
  });

  const [monatsFehler, setMonatsFehler] = useState(null);
  const monatsZug = useMutation({
    mutationFn: ({ art }) =>
      art === 'submit'
        ? selfApi.submitMonth({ year: jahr, month: monat })
        : selfApi.withdrawMonth({ year: jahr, month: monat }),
    onSuccess: () => {
      setMonatsFehler(null);
      refresh();
    },
    /* Die Meldung des Dienstes durchreichen, nicht ersetzen. Sie nennt den
       Ausweg ("die vorgesetzte Person muss ihn wieder öffnen") - eine
       eigene Ersatzmeldung liesse den Mitarbeitenden ratlos zurück. */
    onError: (err) => setMonatsFehler(err?.message || t('common.loadError')),
  });

  if (profile.error) {
    const code = profile.error?.code;
    return (
      <div className="alert warning">
        {code === 'NO_EMPLOYEE_LINK' ? t('self.noLink') : profile.error.message}
      </div>
    );
  }

  function openFor(datum) {
    setForm({ ...LEER, workDate: datum });
    setError(null);
    setOpen(true);
  }

  const gültig =
    form.workDate &&
    (modus === 'clock' ? form.startTime && form.endTime : Number(form.hours) > 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('time.my.title')}</h1>
          <p className="muted">{t('time.my.subtitle')}</p>
        </div>
        {!nurStempel && (
          <Button variant="primary" onClick={() => openFor(heute())} disabled={!profile.data}>
            {t('time.add.cta')}
          </Button>
        )}
      </div>

      <div className="week-nav">
        <Button size="sm" variant="ghost" onClick={() => setFrom(shift(from, -7))}>
          {t('time.week.prev')}
        </Button>
        <span className="mono text-sm">
          {from} - {to}
        </span>
        <Button size="sm" variant="ghost" onClick={() => setFrom(shift(from, 7))}>
          {t('time.week.next')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setFrom(mondayOf(heute()))}>
          {t('time.week.thisWeek')}
        </Button>
      </div>

      {/* Der Gesamtstand ÜBER dem Monat: er rahmt ihn, so wie der Monat die
          Woche rahmt. Wer auf diese Seite kommt, fragt zuerst "wie viel habe
          ich zugute" und erst danach "was war letzte Woche". */}
      {/* Die Uhr zuerst. Wer auf diese Seite kommt, will meistens stempeln -
          und erst danach wissen, wie viel er zugute hat. */}
      <Stempeluhr />

      <TimeAccountCard account={account.data} loading={account.isLoading} />

      <MonthBar
        month={month.data}
        busy={monatsZug.isPending}
        onSubmit={() => monatsZug.mutate({ art: 'submit' })}
        onWithdraw={() => monatsZug.mutate({ art: 'withdraw' })}
      />
      {monatsFehler && <div className="alert danger">{monatsFehler}</div>}

      <BalanceTiles summary={week.data} />

      {week.isLoading ? (
        <p className="muted">{t('time.week.loading')}</p>
      ) : (
        <WeekGrid week={week.data} onAddDay={nurStempel ? undefined : openFor} />
      )}

      <Modal
        open={open}
        title={t('time.add.title')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => add.mutate()} disabled={!gültig || add.isPending}>
              {add.isPending ? t('time.add.saving') : t('time.add.submit')}
            </Button>
          </>
        }
      >
        <label className="field">
          <span className="field-label">{t('time.add.date')}</span>
          <input
            className="input"
            type="date"
            value={form.workDate}
            onChange={(e) => setForm((p) => ({ ...p, workDate: e.target.value }))}
          />
        </label>

        {/* Zwei Wege, sichtbar nebeneinander. Wer nachträgt, soll keine
            Uhrzeiten erfinden müssen; wer stempelt, keine Zahl rechnen. */}
        <div className="segmented">
          <button
            type="button"
            className={modus === 'clock' ? 'active' : undefined}
            onClick={() => setModus('clock')}
          >
            {t('time.add.modeClock')}
          </button>
          <button
            type="button"
            className={modus === 'hours' ? 'active' : undefined}
            onClick={() => setModus('hours')}
          >
            {t('time.add.modeHours')}
          </button>
        </div>

        {modus === 'clock' ? (
          <>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('time.add.start')}</span>
                <input
                  className="input"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                />
              </label>
              <label className="field">
                <span className="field-label">{t('time.add.end')}</span>
                <input
                  className="input"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                />
              </label>
              <label className="field" style={{ maxWidth: '7rem' }}>
                <span className="field-label">{t('time.add.break')}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={form.breakMinutes}
                  onChange={(e) => setForm((p) => ({ ...p, breakMinutes: e.target.value }))}
                />
              </label>
            </div>

            {/* Ausdrücklich, nicht geraten: "Ende vor Start" heisst bei einem
                Vertipper etwas anderes als bei einer Nachtschicht. */}
            <label className="pf-role-option" style={{ maxWidth: '18rem' }}>
              <input
                type="checkbox"
                checked={form.crossesMidnight}
                onChange={(e) => setForm((p) => ({ ...p, crossesMidnight: e.target.checked }))}
              />
              <span>{t('time.add.overnight')}</span>
            </label>
          </>
        ) : (
          <label className="field">
            <span className="field-label">{t('time.add.hours')}</span>
            <input
              className="input"
              type="number"
              step="0.25"
              min="0"
              max="24"
              value={form.hours}
              onChange={(e) => setForm((p) => ({ ...p, hours: e.target.value }))}
            />
          </label>
        )}

        {error && <div className="alert danger">{error}</div>}
      </Modal>
    </>
  );
}
